// 프로필(포토카드) 편집 권한 설정 인터페이스
export interface IPhotocardEditPermission {
  id?: string;
  // 멤버가 편집 가능한 항목들
  addProfile: boolean; // 프로필 추가
  // 기본 정보 세분화
  titleEdit: boolean; // 타이틀 편집
  subtitleEdit: boolean; // 서브타이틀 편집
  descEdit: boolean; // 설명 편집
  thumbnail: boolean; // 대표 이미지
  hoverThumbnail: boolean; // 마우스 호버 이미지
  bgmURL: boolean; // BGM 설정
  soundURL: boolean; // 보이스 파일
  accessType: boolean; // 공개설정
  // 옷장 표시 설정 (on=표시, off=숨김)
  closetShowTag: boolean; // 타이틀 태그 표시
  closetShowSubTitle: boolean; // 서브 타이틀 표시
  closetShowDescription: boolean; // 설명 표시
  closetShowStandingDialogue: boolean; // 스탠딩 대사 · 표정 관리 표시
  tabDesign: boolean; // 이 탭 구성&디자인 버튼
  tabEdit: boolean; // 탭 편집 버튼
  standingSetting: boolean; // 캐릭터 스탠딩 설정 버튼
  statEdit: boolean; // 캐릭터 스탯 설정 버튼
  closetStandingTab: boolean; // 옷장별 스탠딩 대사 설정 탭
  giftItem: boolean; // 아이템 선물하기 버튼
  // 🆕 전역 설정 (권한보다 우선)
  deleteProfile: boolean; // 포토카드 삭제 버튼 표시 여부
  updatedAt?: any;
  // 하위 호환성 (deprecated)
  basicInfo?: boolean;
}

// 기본값: 대부분 허용
export const DEFAULT_PHOTOCARD_EDIT_PERMISSION: IPhotocardEditPermission = {
  addProfile: true, // 기본적으로 프로필 추가 허용
  titleEdit: true, // 타이틀 편집 허용
  subtitleEdit: true, // 서브타이틀 편집 허용
  descEdit: true, // 설명 편집 허용
  thumbnail: true,
  hoverThumbnail: true,
  bgmURL: true,
  soundURL: true,
  accessType: false, // 기본적으로 비허용 (관리자 전용)
  closetShowTag: true, // 기본적으로 타이틀 태그 표시
  closetShowSubTitle: true, // 기본적으로 서브 타이틀 표시
  closetShowDescription: true, // 기본적으로 설명 표시
  closetShowStandingDialogue: true, // 기본적으로 스탠딩 대사 · 표정 관리 표시
  tabDesign: false, // 기본적으로 비허용
  tabEdit: false, // 기본적으로 비허용
  standingSetting: false, // 기본적으로 비허용
  statEdit: false, // 기본적으로 비허용
  closetStandingTab: false, // 기본적으로 비허용
  giftItem: true, // 기본적으로 선물하기 버튼 표시
  deleteProfile: true, // 기본적으로 삭제 버튼 표시
};

// 권한 필드 라벨 매핑 (멤버에게 노출되는 항목만)
export const PHOTOCARD_EDIT_PERMISSION_LABELS: Record<
  keyof Omit<IPhotocardEditPermission, 'id' | 'updatedAt' | 'basicInfo'>,
  { label: string; desc: string; isGlobal?: boolean }
> = {
  addProfile: {
    label: '프로필 추가',
    desc: '새 캐릭터/프로필 생성 허용',
  },
  titleEdit: {
    label: '타이틀 편집',
    desc: '프로필 타이틀(이름) 수정',
  },
  subtitleEdit: {
    label: '서브타이틀 편집',
    desc: '프로필 서브타이틀 수정',
  },
  descEdit: {
    label: '설명 편집',
    desc: '프로필 설명 텍스트 수정',
  },
  thumbnail: {
    label: '대표 이미지',
    desc: '프로필 메인 이미지',
  },
  hoverThumbnail: {
    label: '마우스 호버 이미지',
    desc: '마우스 오버 시 표시되는 이미지',
  },
  bgmURL: {
    label: 'BGM 설정',
    desc: 'YouTube 링크로 배경음악 설정',
  },
  soundURL: {
    label: '보이스 파일',
    desc: '음성 파일 업로드',
  },
  accessType: {
    label: '공개설정',
    desc: '프로필 공개/비공개/비밀번호 설정',
  },
  closetShowTag: {
    label: '타이틀 태그',
    desc: '옷장 상단의 타이틀 태그 표시 여부',
  },
  closetShowSubTitle: {
    label: '서브 타이틀',
    desc: '옷장 상단의 서브 타이틀 표시 여부',
  },
  closetShowDescription: {
    label: '설명',
    desc: '옷장 상단의 설명 텍스트 표시 여부',
  },
  closetShowStandingDialogue: {
    label: '스탠딩 대사 · 표정 관리',
    desc: '스탠딩 대사 오버레이 및 표정 관리 기능 표시 여부',
  },
  tabDesign: {
    label: '이 탭 구성&디자인 버튼',
    desc: '프로필 탭 디자인 편집 접근',
  },
  tabEdit: {
    label: '탭 편집 버튼',
    desc: '프로필 탭 내용 편집 접근',
  },
  standingSetting: {
    label: '캐릭터 스탠딩 설정 버튼',
    desc: '캐릭터 스탠딩 & 대사 설정 접근',
  },
  statEdit: {
    label: '캐릭터 스탯 설정 버튼',
    desc: '캐릭터 HP/MP 및 스탯 수치 수정 접근',
  },
  closetStandingTab: {
    label: '스탠딩 대사 설정 탭',
    desc: '편집 모드에서 옷장별 스탠딩 대사 설정 탭 접근',
  },
  giftItem: {
    label: '아이템 선물하기',
    desc: '인벤토리에서 아이템 선물하기 버튼 표시 여부',
  },
  deleteProfile: {
    label: '포토카드 삭제 버튼',
    desc: '프로필 삭제 버튼 표시 (권한 무관, 전역 설정)',
    isGlobal: true,
  },
};

export const PHOTOCARD_EDIT_PERMISSION_DOC = 'setting/photocardEditPermission';
